import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useBluelagoon, useTaskWebSocket } from "@bluelagoon";
import { useWorkflow } from "./WorkflowContext";
import useStorage from "../hooks/useStorage";
import useStorageByKey from "../hooks/useStorageByKey";
import { trackWorkflowUsage } from "@utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

const WorkflowExecutionContext = createContext();

const toErrorString = (err) => {
    if (err == null) return "Unknown error";
    if (typeof err === "string") return err;
    if (typeof err === "object") {
        if (typeof err.error === "string") return err.error;
        if (typeof err.message === "string") return err.message;
        if (err.error && typeof err.error === "object") return toErrorString(err.error);
        if (err.message && typeof err.message === "object") return toErrorString(err.message);
        try {
            return JSON.stringify(err);
        } catch {
            return "Unknown error";
        }
    }
    return String(err);
};

function useProvideWorkflowExecution({ workflowId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [executionId, setExecutionId] = useStorageByKey(
        `executionId-${workflowId}`,
        null,
        window.localStorage
    );

    const [executionStatus, setExecutionStatus] = useState(null);
    const [stepExecutionResult, setStepExecutionResult] = useState(null);
    const [usageWarning, setUsageWarning] = useState(null);

    const [stepExecutionLogs, setStepExecutionLogs] = useState({});
    const [workflowDatabases, setWorkflowDatabases] = useState([]);
    const [recentDatabases, setRecentDatabases] = useStorage(
        `recentDatabases`,
        {},
        window.localStorage
    );
    const [stepIdRunning, setStepIdRunning] = useState(null);
    const [completedStepIds, setCompletedStepIds] = useState(new Set());
    const [failedStepId, setFailedStepId] = useState(null);
    const [failedStepError, setFailedStepError] = useState(null);
    const [pausedStepId, setPausedStepId] = useState(null);
    const { workflow, steps, testData, setTestData } = useWorkflow();
    const didFetchExecutionData = useRef(false);
    const didFetchDatabases = useRef(false);
    const workflowRef = useRef(false);
    const {
        call: startWorkflowCall,
        isLoading: isStartWorkflowLoading,
        error: startWorkflowError,
    } = useBluelagoon({
        method: "startWorkflow",
    });
    const {
        call: getWorkflowExecutionByIdCall,
        isLoading: isGetWorkflowExecutionByIdLoading,
        error: getWorkflowExecutionByIdError,
    } = useBluelagoon({
        method: "getWorkflowExecutionById",
    });

    const {
        call: getWorkflowDatabasesCall,
        response: databasesResponse,
        isLoading: isDatabasesLoading,
        error: databasesError,
    } = useBluelagoon({
        method: "getWorkflowDatabases",
    });

    const { call: getTaskStatusCall } = useBluelagoon({
        method: "getTaskStatus",
    });

    const { call: cancelWorkflowExecutionCall } = useBluelagoon({
        method: "cancelWorkflowExecution",
    });

    const [activeTaskId, setActiveTaskId] = useState(null);
    const taskResolverRef = useRef(null);
    const fallbackPollRef = useRef(null);

    const handleGetTaskStatus = React.useCallback(
        async (taskId) => {
            return getTaskStatusCall({ taskId });
        },
        [getTaskStatusCall]
    );

    // Fetch databases whenever workflow starts or when workflowId changes
    const fetchDatabases = React.useCallback(
        async (updateRecentDatabases = false) => {
            try {
                const response = await getWorkflowDatabasesCall({ workflowId });
                if (response.data?.databases) {
                    setWorkflowDatabases(response.data.databases);

                    if (
                        updateRecentDatabases &&
                        response.data.databases?.some(
                            (db) => db.isActiveWorkflowDatabase
                        )
                    ) {
                        setRecentDatabases({
                            databases: response.data.databases?.filter(
                                (db) => db.isActiveWorkflowDatabase
                            ),
                            workflowId: workflowId,
                        });
                    }
                }
            } catch (err) {
                console.error("Error fetching workflow databases:", err);
            }
        },
        [workflowId, getWorkflowDatabasesCall, setRecentDatabases]
    );

    const handleResetRecentDatabases = React.useCallback(() => {
        setRecentDatabases({});
    }, []);

    // Track which partialFeedback entries we already processed so we
    // don't replay old events on every poll / WS update.
    const processedFeedbackRef = useRef(0);

    const processPartialFeedback = useCallback(
        (feedback) => {
            if (!Array.isArray(feedback) || feedback.length === 0) return;

            // Only process new entries since last call
            const startIdx = processedFeedbackRef.current;
            if (startIdx >= feedback.length) return;
            const newEvents = feedback.slice(startIdx);
            processedFeedbackRef.current = feedback.length;

            for (const event of newEvents) {
                if (!event?.type) continue;

                if (event.type === "step:started") {
                    setStepIdRunning(event.stepId);
                    setFailedStepId(null);
                    setFailedStepError(null);
                } else if (event.type === "step:completed") {
                    // Track this step as completed so the canvas shows it
                    // as success even before the task finishes.
                    if (event.stepId) {
                        setCompletedStepIds((prev) => {
                            const next = new Set(prev);
                            next.add(event.stepId);
                            return next;
                        });
                    }
                } else if (event.type === "step:failed") {
                    setStepIdRunning(null);
                    setFailedStepId(event.stepId);
                    setFailedStepError(
                        event.error ? toErrorString(event.error) : "Step execution failed"
                    );
                } else if (event.type === "step:skipped") {
                    // Same as step:completed — let next step:started take over
                }
            }
        },
        [setStepIdRunning, setFailedStepId, setFailedStepError]
    );

    const processTaskData = useCallback(
        async (taskData, stepId) => {
            // Process intermediate step events from partialFeedback
            if (taskData.partialFeedback) {
                processPartialFeedback(taskData.partialFeedback);
            }

            if (taskData.status === "COMPLETED") {
                await fetchDatabases(true);

                if (taskData.error) {
                    setFailedStepId(stepId);
                    setFailedStepError(toErrorString(taskData.error));
                }

                if (taskData.logs) {
                    setStepExecutionLogs((prev) => {
                        const logs = taskData.logs;
                        // Engine now returns logs as { stepId: [...] } map
                        if (logs && typeof logs === "object" && !Array.isArray(logs)) {
                            return { ...prev, ...logs };
                        }
                        // Legacy flat array fallback
                        return { ...prev, [stepId]: logs };
                    });
                }

                if (taskData.contextData) {
                    setStepExecutionResult(taskData.contextData);
                }

                if (taskData.executionId) {
                    setExecutionId(taskData.executionId);
                }

                // Auto-open the paused step (e.g. HTML form waiting for user input)
                if (taskData.paused && taskData.stepId) {
                    setPausedStepId(taskData.stepId);
                } else {
                    setPausedStepId(null);
                }

                // Reset feedback counter for next execution
                processedFeedbackRef.current = 0;
                setStepIdRunning(null);
                setCompletedStepIds(new Set());
                return true;
            } else if (taskData.status === "FAILED") {
                processedFeedbackRef.current = 0;
                setStepIdRunning(null);
                setCompletedStepIds(new Set());
                setFailedStepId(stepId);
                setFailedStepError(
                    toErrorString(taskData.error ?? taskData.message)
                );
                return true;
            }
            return false;
        },
        [fetchDatabases, setStepExecutionLogs, setStepExecutionResult, setExecutionId, setStepIdRunning, setFailedStepId, setFailedStepError, processPartialFeedback, setPausedStepId]
    );

    // WebSocket handler for execution task updates
    const activeStepIdRef = useRef(null);

    // Helper to resolve the task and clean up
    const resolveTask = useCallback((responseData) => {
        if (fallbackPollRef.current) {
            clearInterval(fallbackPollRef.current);
            fallbackPollRef.current = null;
        }
        setActiveTaskId(null);
        activeStepIdRef.current = null;
        if (taskResolverRef.current) {
            taskResolverRef.current(responseData);
            taskResolverRef.current = null;
        }
    }, []);

    const handleWsUpdate = useCallback(
        async (data) => {
            const taskData = data?.data || data;
            const stepId = activeStepIdRef.current;

            const isTerminal = await processTaskData(taskData, stepId);
            if (isTerminal) {
                resolveTask({ data: taskData });
            }
        },
        [processTaskData, resolveTask]
    );

    const { isConnected: wsConnected } = useTaskWebSocket({
        taskId: activeTaskId,
        enabled: !!activeTaskId,
        onUpdate: handleWsUpdate,
    });

    // Fallback polling — slower when WS connected, faster when not
    useEffect(() => {
        if (!activeTaskId) return;

        if (fallbackPollRef.current) {
            clearInterval(fallbackPollRef.current);
        }

        const pollInterval = wsConnected ? 10000 : 2000;

        const poll = async () => {
            try {
                const response = await handleGetTaskStatus(activeTaskId);
                if (response?.data) {
                    const stepId = activeStepIdRef.current;
                    const isTerminal = await processTaskData(response.data, stepId);
                    if (isTerminal) {
                        resolveTask(response);
                    }
                }
            } catch (error) {
                console.error("Error polling task status:", error);
            }
        };

        // Immediate first poll
        poll();

        fallbackPollRef.current = setInterval(poll, pollInterval);

        return () => {
            clearInterval(fallbackPollRef.current);
            fallbackPollRef.current = null;
        };
    }, [activeTaskId, wsConnected, handleGetTaskStatus, processTaskData, resolveTask]);

    // Timeout safety net — 20 minutes
    useEffect(() => {
        if (!activeTaskId) return;

        const timeout = setTimeout(() => {
            const stepId = activeStepIdRef.current;
            setStepIdRunning(null);
            if (stepId) setFailedStepId(stepId);
            setFailedStepError("Task execution timeout after 20 minutes");
            resolveTask({ data: { status: "FAILED", error: "Timeout" } });
        }, 20 * 60 * 1000);

        return () => clearTimeout(timeout);
    }, [activeTaskId, resolveTask]);

    const waitForTask = useCallback(
        (taskId, stepId) => {
            return new Promise((resolve) => {
                activeStepIdRef.current = stepId;
                taskResolverRef.current = resolve;
                setActiveTaskId(taskId);
            });
        },
        []
    );

    const cancelExecution = React.useCallback(async () => {
        if (!executionId) return;
        try {
            await cancelWorkflowExecutionCall({ executionId });
            setStepIdRunning(null);
            setFailedStepId(null);
            setFailedStepError(null);
            // Resolve the active task so polling stops
            resolveTask({ data: { status: "CANCELLED" } });
        } catch (err) {
            console.error("Error cancelling execution:", err);
        }
    }, [executionId, cancelWorkflowExecutionCall, resolveTask]);

    const handleResetFailedStep = React.useCallback(() => {
        setFailedStepId(null);
        setFailedStepError(null);
    }, []);

    const handleStepTestResults = React.useCallback(
        (stepId, results, logs) => {
            setStepExecutionResult((prev) => {
                const stepIndex = steps.findIndex((step) => step.id === stepId);

                let newStepExecutionResult = prev;

                if (!newStepExecutionResult) {
                    newStepExecutionResult = [];
                }

                if (stepIndex !== -1) {
                    newStepExecutionResult[stepIndex] = results;
                }

                return newStepExecutionResult;
            });

            setStepExecutionLogs((prev) => ({
                ...prev,
                [stepId]: logs,
            }));
        },
        [steps]
    );

    // Initial fetch of databases
    useEffect(() => {
        if (workflowId) {
            if (didFetchDatabases.current !== workflowId) {
                didFetchDatabases.current = workflowId;
                fetchDatabases();
            }
        } else if (didFetchDatabases.current !== "new") {
            fetchDatabases();
            didFetchDatabases.current = "new";
        }
    }, [workflowId]);

    // Fetch execution data when executionId is set
    useEffect(() => {
        const fetchExecutionData = async () => {
            // Only run if executionId exists in localStorage

            if (executionId && didFetchExecutionData.current !== executionId) {
                didFetchExecutionData.current = executionId;
                try {
                    const response = await getWorkflowExecutionByIdCall({
                        executionId,
                        workflowId,
                    });

                    if (response.data) {
                        setExecutionStatus(response.data.status);

                        // Load initial data from execution (e.g. coming from monitoring)
                        if (response.data.initialData) {
                            setTestData(response.data.initialData);
                        }

                        // Process step executions if available
                        if (response.data.contextData) {
                            setStepExecutionResult(response.data.contextData);

                            setStepExecutionLogs(response.data.logs ?? {});
                        }
                    }
                } catch (err) {
                    console.error("Error fetching execution data:", err);
                }
            } else if (executionId == null) {
                didFetchExecutionData.current = null;
                handleResetExecution();
            }
        };

        fetchExecutionData();
    }, [executionId]); // Empty dependency array to run only once on mount

    const startWorkflow = React.useCallback(
        async (params, stepId = null, executionIdForPreview = null) => {
            try {
                // Fetch latest database information before starting

                let finalExecution = executionId;
                if (executionIdForPreview) {
                    finalExecution = executionIdForPreview;
                }

                processedFeedbackRef.current = 0;
                setCompletedStepIds(new Set());
                setStepIdRunning(stepId);
                setFailedStepId(null);
                setFailedStepError(null);

                // Track workflow usage when workflow is started
                trackWorkflowUsage(workflowId);

                const response = await startWorkflowCall({
                    id: workflowId,
                    stepData: params,
                    initialData: testData,
                    stepId,
                    executionId: finalExecution,
                });

                // Handle automation limit returned as response (non-thrown)
                const respError = response?.data?.error || response?.error;
                if (respError === "AUTOMATION_LIMIT_REACHED" || respError === "AUTOMATION_CONCURRENCY_LIMIT") {
                    const usage = response?.data?.usage ?? response?.usage;
                    const respLimit = response?.data?.limit ?? response?.limit;
                    setStepIdRunning(null);
                    setFailedStepId(stepId);
                    toast.error(
                        t("common.automationLimitReached.title"),
                        {
                            description: t("common.automationLimitReached.description", { usage, limit: respLimit }),
                            duration: 8000,
                            action: {
                                label: t("common.automationLimitReached.upgradePlan"),
                                onClick: () => {
                                    localStorage.setItem("billing_return_url", location.pathname + location.search);
                                    navigate("/billing");
                                },
                            },
                        }
                    );
                    setUsageWarning({ status: "blocked", usage, limit: respLimit });
                    setFailedStepError(t("common.automationLimitReached.description", { usage, limit: respLimit }));
                    return response;
                }

                // Capture usage warning from response (piggybacked, no extra API call)
                const warning = response?.data?.usageWarning || response?.usageWarning;
                if (warning) {
                    setUsageWarning(warning);
                }

                // Check if this is a background task response
                if (response.data?.taskId) {
                    return await waitForTask(
                        response.data.taskId,
                        stepId
                    );
                }

                // Handle legacy response format (if any)
                if (response.data?.logs) {
                    setStepExecutionLogs((prev) => {
                        const logs = response.data?.logs;
                        // Engine returns logs as { stepId: [...] } map
                        if (logs && typeof logs === "object" && !Array.isArray(logs)) {
                            return { ...prev, ...logs };
                        }
                        // Legacy flat array fallback
                        return { ...prev, [stepId]: logs };
                    });
                }

                if (response.data?.contextData) {
                    setStepExecutionResult(response.data?.contextData);
                }

                if (response.data?.status === "failed") {
                    setStepIdRunning(null);
                    setFailedStepId(stepId);
                    return response;
                }

                if (response.data?.executionId) {
                    setExecutionId(response.data?.executionId);
                }

                // Auto-open paused step (e.g. HTML form)
                if (response.data?.paused && response.data?.stepId) {
                    setPausedStepId(response.data.stepId);
                }

                setStepIdRunning(null);
                setFailedStepId(null);
                return response;
            } catch (err) {
                console.error("Error running workflow:", err);
                setStepIdRunning(null);
                setFailedStepId(stepId);

                const errorCode = err?.data?.error || err?.error;
                if (errorCode === "AUTOMATION_LIMIT_REACHED") {
                    const usage = err?.data?.usage ?? err?.usage;
                    const limit = err?.data?.limit ?? err?.limit;
                    toast.error(
                        t("common.automationLimitReached.title"),
                        {
                            description: t("common.automationLimitReached.description", { usage, limit }),
                            duration: 8000,
                            action: {
                                label: t("common.automationLimitReached.upgradePlan"),
                                onClick: () => {
                                    localStorage.setItem("billing_return_url", location.pathname + location.search);
                                    navigate("/billing");
                                },
                            },
                        }
                    );
                    setUsageWarning({ status: "blocked", usage, limit });
                    setFailedStepError(t("common.automationLimitReached.description", { usage, limit }));
                    throw err;
                }

                const errorMessage = err?.data?.message
                    || err?.message
                    || err?.error
                    || "Workflow execution failed";
                setFailedStepError(toErrorString(errorMessage));
                throw err;
            }
        },
        [
            workflowId,
            startWorkflowCall,
            executionId,
            fetchDatabases,
            testData,
            waitForTask,
            t,
            navigate,
            location,
        ]
    );

    React.useEffect(() => {
        if (workflow?.workflowSteps) {
            if (
                workflow?.workflowSteps?.[0]?.type === "CODE_EDITOR" &&
                workflow?.workflowSteps?.[0]?.config?.language === "html"
            ) {
                setStepExecutionResult((prev) => {
                    // Only initialize if no results exist yet — don't overwrite runtime data
                    if (prev && prev.length > 0) return prev;
                    return [
                        {
                            stepId: workflow?.workflowSteps?.[0]?.id,
                            result: workflow?.publicKey,
                        },
                    ];
                });
            }
        }
    }, [workflow]);

    const handleResetExecution = React.useCallback(() => {
        setExecutionId(null);
        setExecutionStatus(null);
        setStepExecutionResult(null);
        setStepExecutionLogs({});
    }, []);

    const removeStepExecutionData = React.useCallback(
        (stepId) => {
            // Remove from stepExecutionLogs object
            setStepExecutionLogs((prev) => {
                const newLogs = { ...prev };
                delete newLogs[stepId];
                return newLogs;
            });

            // Remove from stepExecutionResult array by finding the step index
            setStepExecutionResult((prev) => {
                if (!prev || !Array.isArray(prev)) return prev;

                const stepIndex = steps.findIndex((step) => step.id === stepId);
                if (stepIndex === -1) return prev;

                // Create new array without the element at stepIndex
                const newResult = [...prev];
                newResult.splice(stepIndex, 1);
                return newResult;
            });
        },
        [steps]
    );

    const handleSetLogs = React.useCallback((logs) => {
        setStepExecutionLogs((prev) => ({
            ...prev,
            [stepId]: logs,
        }));
    }, []);

    // React.useEffect(() => {
    //     const previousWorkflowId = workflowRef.current;
    //     const hasWorkflowChanged = previousWorkflowId !== workflowId;

    //     if (hasWorkflowChanged) {
    //         if (previousWorkflowId != null || workflowId != null) {
    //             console.log("workflowId", workflowId);
    //             const executionId = window.localStorage.getItem(
    //                 `executionId-${workflowId}`
    //             );
    //             console.log("executionId", executionId);
    //             if (executionId) {
    //                 setExecutionId(JSON.parse(executionId));
    //             } else {
    //                 setExecutionId(null);
    //             }
    //         }

    //         workflowRef.current = workflowId ?? null;
    //     }
    // }, [workflowId]);

    return {
        executionId,
        executionStatus,
        stepExecutionResult,
        stepExecutionLogs,
        startWorkflow,
        isStartWorkflowLoading,
        startWorkflowError,
        workflowDatabases,
        isDatabasesLoading,
        databasesError,
        refreshDatabases: fetchDatabases,
        databasesResponse,
        handleGetTaskStatus,
        handleStepTestResults,
        setExecutionId,
        stepIdRunning,
        completedStepIds,
        isExecutingTask: !!activeTaskId,
        failedStepId,
        handleResetExecution,
        removeStepExecutionData,
        handleSetLogs,
        setStepExecutionLogs,
        failedStepError,
        handleResetFailedStep,
        cancelExecution,
        recentDatabases,
        handleResetRecentDatabases,
        pausedStepId,
        setPausedStepId,
        usageWarning,
    };
}

export function ProvideWorkflowExecution({ children, workflowId }) {
    const workflow = useProvideWorkflowExecution({ workflowId });
    return (
        <WorkflowExecutionContext.Provider value={workflow}>
            {children}
        </WorkflowExecutionContext.Provider>
    );
}

export function useWorkflowExecution() {
    return useContext(WorkflowExecutionContext);
}
